import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useEntityPresentation } from '@backstage/plugin-catalog-react';
import { DEFAULT_NAMESPACE } from '@backstage/catalog-model';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import SvgIcon from '@material-ui/core/SvgIcon';
import Tooltip from '@material-ui/core/Tooltip';
import classNames from 'classnames';
import { fetchHealthSummary } from './api';

/**
 * Health-colored catalog graph nodes: entities with ping checks get a status
 * glow ring and dot (green up / red down) from the datadog-graphs
 * health-summary endpoint. Everything else renders exactly like the stock
 * node (the geometry below mirrors the plugin's DefaultRenderNode, which is
 * not exported).
 */

const HealthSummaryContext = createContext<Record<string, string>>({});

/** Fetches the org-wide ping summary once and shares it with graph nodes. */
export function GraphHealthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [statuses, setStatuses] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetchHealthSummary({ discoveryApi, fetchApi })
        .then(result => {
          if (!cancelled) {
            setStatuses(result);
          }
        })
        .catch(() => undefined);
    load();
    const interval = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [discoveryApi, fetchApi]);

  return (
    <HealthSummaryContext.Provider value={statuses}>
      {children}
    </HealthSummaryContext.Provider>
  );
}

/**
 * The org-wide health summary, keyed by LOWERCASED entity ref. Must be used
 * inside a GraphHealthProvider, which owns the single 60s-cached fetch.
 */
export function useHealthSummary(): Record<string, string> {
  return useContext(HealthSummaryContext);
}

const useNameBoxStyles = makeStyles(
  {
    // The color comes in via a CSS variable so one keyframe set serves both
    // the green and red variants.
    '@keyframes savrHealthGlow': {
      '0%': { boxShadow: '0 0 3px var(--savr-health-color)' },
      '50%': { boxShadow: '0 0 10px var(--savr-health-color)' },
      '100%': { boxShadow: '0 0 3px var(--savr-health-color)' },
    },
    box: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '1px 8px',
      borderRadius: 6,
      animation: '$savrHealthGlow 2.4s ease-in-out infinite',
    },
  },
  { name: 'SavrHealthNameBox' },
);

/**
 * The health colour for a status, or undefined where there is nothing to say.
 *
 * Above its callers rather than below them: it is a `const`, so it is not
 * hoisted, and it only worked from HealthNameBox because that call happens
 * inside a component body rather than at module evaluation. Both readers are
 * below this point now.
 */
const statusColorOf = (status: string | undefined, dark: boolean) => {
  if (status === 'ok') {
    return dark ? '#34D399' : '#059669';
  }
  if (status === 'crit') {
    return dark ? '#FF385F' : '#E11D48';
  }
  return undefined;
};

/**
 * Wraps an entity name in a pulsating green/red box when the entity has ping
 * checks; renders children untouched otherwise. Used by relation-table name
 * cells; must be used inside a GraphHealthProvider.
 */
export function HealthNameBox({
  entityRef,
  children,
}: {
  entityRef: string;
  children: React.ReactNode;
}) {
  const classes = useNameBoxStyles();
  const theme = useTheme();
  const statuses = useContext(HealthSummaryContext);
  const status = statuses[entityRef.toLocaleLowerCase('en-US')];
  const color = statusColorOf(status, theme.palette.type === 'dark');
  if (!color) {
    return <>{children}</>;
  }
  return (
    <Tooltip
      arrow
      title={
        status === 'ok'
          ? "Live health: all of this service's configured health checks are passing"
          : "Live health: one or more of this service's configured health checks are failing"
      }
    >
      <span
        className={classes.box}
        style={
          {
            border: `1px solid ${color}66`,
            backgroundColor: `${color}14`,
            '--savr-health-color': `${color}59`,
          } as React.CSSProperties
        }
      >
        {children}
      </span>
    </Tooltip>
  );
}

const useStyles = makeStyles(
  theme => ({
    node: {
      fill: theme.palette.grey[300],
      stroke: theme.palette.grey[300],
      '&.primary': {
        fill: theme.palette.primary.light,
        stroke: theme.palette.primary.light,
      },
      '&.secondary': {
        fill: theme.palette.secondary.light,
        stroke: theme.palette.secondary.light,
      },
    },
    text: {
      fill: theme.palette.getContrastText(theme.palette.grey[300]),
      '&.primary': {
        fill: theme.palette.primary.contrastText,
      },
      '&.secondary': {
        fill: theme.palette.secondary.contrastText,
      },
      '&.focused': {
        fontWeight: 'bold',
      },
    },
    clickable: {
      cursor: 'pointer',
    },
  }),
  { name: 'SavrGraphHealthNode' },
);

// Matches the graph node prop shape (EntityNode from plugin-catalog-graph).
type NodeProps = {
  node: {
    id: string;
    entity?: any;
    color?: 'primary' | 'secondary' | 'default';
    focused?: boolean;
    onClick?: (event: any) => void;
    kind?: string;
    name?: string;
    namespace?: string;
  };
};

export function HealthRenderNode({ node }: NodeProps) {
  const { id, entity, color = 'default', focused, onClick } = node;
  const classes = useStyles();
  const theme = useTheme();
  const statuses = useContext(HealthSummaryContext);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const idRef = useRef<SVGTextElement | null>(null);

  const presentation = useEntityPresentation(entity ?? id, {
    defaultNamespace: DEFAULT_NAMESPACE,
  });

  useLayoutEffect(() => {
    if (idRef.current) {
      const box = idRef.current.getBBox();
      const renderedHeight = Math.round(box.height);
      const renderedWidth = Math.round(box.width);
      if (renderedHeight !== height || renderedWidth !== width) {
        setWidth(renderedWidth);
        setHeight(renderedHeight);
      }
    }
  }, [width, height]);

  const status = useMemo(() => {
    const ref = (presentation.entityRef ?? id).toLocaleLowerCase('en-US');
    return statuses[ref];
  }, [statuses, presentation.entityRef, id]);
  const statusColor = statusColorOf(status, theme.palette.type === 'dark');

  // presentation.Icon is IconComponent | false - || strips both.
  const Icon = presentation.Icon || SvgIcon;
  const hasKindIcon = Boolean(presentation.Icon);
  const padding = 10;
  const iconSize = height;
  const paddedIconWidth = hasKindIcon ? iconSize + padding : 0;
  const paddedWidth = paddedIconWidth + width + padding * 2;
  const paddedHeight = height + padding * 2;
  const displayTitle = presentation.primaryTitle ?? id;

  return (
    <g onClick={onClick} className={classNames(onClick && classes.clickable)}>
      <rect
        className={classNames(
          classes.node,
          color === 'primary' && 'primary',
          color === 'secondary' && 'secondary',
        )}
        width={paddedWidth}
        height={paddedHeight}
        rx={10}
        style={
          statusColor
            ? {
                stroke: statusColor,
                strokeWidth: 2,
                filter: `drop-shadow(0 0 4px ${statusColor})`,
              }
            : undefined
        }
      />
      {statusColor && (
        <circle
          cx={paddedWidth - 2}
          cy={2}
          r={5}
          fill={statusColor}
          stroke={theme.palette.background.default}
          strokeWidth={1.5}
        />
      )}
      {hasKindIcon && (
        <Icon
          y={padding}
          x={padding}
          width={iconSize}
          height={iconSize}
          style={{ width: iconSize, height: iconSize, fontSize: iconSize }}
          className={classNames(
            classes.text,
            focused && 'focused',
            color === 'primary' && 'primary',
            color === 'secondary' && 'secondary',
          )}
        />
      )}
      <text
        ref={idRef}
        className={classNames(
          classes.text,
          focused && 'focused',
          color === 'primary' && 'primary',
          color === 'secondary' && 'secondary',
        )}
        y={paddedHeight / 2}
        x={paddedIconWidth + (width + padding * 2) / 2}
        textAnchor="middle"
        alignmentBaseline="middle"
      >
        {displayTitle}
      </text>
      <title>{presentation.entityRef}</title>
    </g>
  );
}
